import { z } from 'zod';

/**
 * §3.1 — Zod şemaları backend ve frontend arasında **paylaşılır**.
 * Böylece formdaki doğrulama ile API'nin doğrulaması birbirinden ayrışamaz.
 * Hata mesajları Türkçe (§19.4).
 */

export const loginSchema = z.object({
  email: z.email({ message: 'Geçerli bir e-posta adresi girin.' }),
  password: z.string().min(1, 'Şifre boş olamaz.'),
  totpCode: z
    .string()
    .regex(/^\d{6}$/, 'Doğrulama kodu 6 haneli olmalı.')
    .optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Mevcut şifrenizi girin.'),
    newPassword: z
      .string()
      .min(12, 'Yeni şifre en az 12 karakter olmalı.')
      .regex(/[a-zçğıöşü]/, 'Yeni şifre en az bir küçük harf içermeli.')
      .regex(/[A-ZÇĞİÖŞÜ]/, 'Yeni şifre en az bir büyük harf içermeli.')
      .regex(/\d/, 'Yeni şifre en az bir rakam içermeli.'),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Şifre tekrarı eşleşmiyor.',
    path: ['confirmPassword'],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const totpVerifySchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Doğrulama kodu 6 haneli olmalı.'),
});
export type TotpVerifyInput = z.infer<typeof totpVerifySchema>;

export const totpDisableSchema = z.object({
  password: z.string().min(1, 'Şifrenizi girin.'),
  code: z.string().regex(/^\d{6}$/, 'Doğrulama kodu 6 haneli olmalı.'),
});
export type TotpDisableInput = z.infer<typeof totpDisableSchema>;

/** Rollerin Türkçe adları: `OWNER` her şeyi yapar · `ADMIN` ayarlar hariç · `VIEWER` salt okunur. */
export const KULLANICI_ROL_ADLARI = {
  OWNER: 'Sahip',
  ADMIN: 'Yönetici',
  VIEWER: 'İzleyici',
} as const;

export const kullaniciRolSchema = z.enum(['OWNER', 'ADMIN', 'VIEWER'], {
  message: 'Geçerli bir rol seçin.',
});
export type KullaniciRol = z.infer<typeof kullaniciRolSchema>;

/**
 * Yalnız SAHİBİN kullandığı kullanıcı ekleme (`POST /kullanicilar`). Kayıt olma değil: uç oturum ve
 * sahip rolü istiyor, geçici şifre hiçbir yanıtta ve kayıtta görünmüyor.
 */
export const kullaniciEkleSchema = z.object({
  email: z.email({ message: 'Geçerli bir e-posta adresi girin.' }),
  name: z.string().trim().min(1, 'Ad boş olamaz.').max(100, 'Ad en fazla 100 karakter olabilir.'),
  role: kullaniciRolSchema,
  geciciSifre: z
    .string()
    .min(12, 'Geçici şifre en az 12 karakter olmalı.')
    .regex(/[a-zçğıöşü]/, 'Geçici şifre en az bir küçük harf içermeli.')
    .regex(/[A-ZÇĞİÖŞÜ]/, 'Geçici şifre en az bir büyük harf içermeli.')
    .regex(/\d/, 'Geçici şifre en az bir rakam içermeli.'),
});
export type KullaniciEkleInput = z.infer<typeof kullaniciEkleSchema>;

export const kullaniciRolDegistirSchema = z.object({ role: kullaniciRolSchema });
export type KullaniciRolDegistirInput = z.infer<typeof kullaniciRolDegistirSchema>;

export const kullaniciDurumSchema = z.object({ isActive: z.boolean() });
export type KullaniciDurumInput = z.infer<typeof kullaniciDurumSchema>;

/** Giriş yanıtı — refresh token gövdede DÖNMEZ, httpOnly cookie'de gider (§23.1). */
export interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  user: {
    id: string;
    email: string;
    name: string;
    role: 'OWNER' | 'ADMIN' | 'VIEWER';
    totpEnabled: boolean;
  };
}
